import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Loader2, Ticket } from "lucide-react";

export default function CodesManager({ codes, isLoading, onCreate, onDelete }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deletingCode, setDeletingCode] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setIsSaving(true);
    const res = await onCreate(code.trim(), name.trim());
    setIsSaving(false);
    if (res.success) {
      setCode("");
      setName("");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Ta bort koden ${c}? Detta går inte att ångra.`)) return;
    setDeletingCode(c);
    await onDelete(c);
    setDeletingCode(null);
  };

  return (
    <div className="space-y-6" data-testid="codes-manager">
      {/* Create form */}
      <form
        onSubmit={handleCreate}
        className="bg-[#0A0A0A] border border-[#222] p-4 md:p-6"
        data-testid="create-code-form"
      >
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-4">
          Skapa ny kod
        </h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="KOD (t.ex. SOMMAR25)"
            maxLength={30}
            className="bg-[#1A1A1A] border border-[#333] text-white placeholder:text-gray-600 focus:border-[#FFD700] h-11 rounded-sm uppercase tracking-widest"
            data-testid="new-code-input"
          />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Namn (valfritt)"
            maxLength={60}
            className="bg-[#1A1A1A] border border-[#333] text-white placeholder:text-gray-600 focus:border-[#FFD700] h-11 rounded-sm"
            data-testid="new-code-name-input"
          />
          <Button
            type="submit"
            disabled={isSaving || !code.trim()}
            className="bg-[#FFD700] text-black font-bold uppercase tracking-wider hover:bg-[#E5C100] rounded-sm h-11 whitespace-nowrap"
            data-testid="create-code-button"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" /> Lägg till
              </>
            )}
          </Button>
        </div>
        <p className="text-gray-600 text-xs mt-3">
          Koder är skiftlägesokänsliga. Endast bokstäver, siffror, - och _ tillåtna. Ändringar gäller direkt utan omstart.
        </p>
      </form>

      {/* Codes list */}
      <div className="bg-[#0A0A0A] border border-[#222]">
        <div className="px-4 md:px-6 py-4 border-b border-[#222]">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#FFD700]">
            Aktiva koder ({codes.length})
          </h3>
        </div>
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">Laddar koder...</div>
        ) : codes.length === 0 ? (
          <div className="p-12 text-center text-gray-600">Inga koder ännu</div>
        ) : (
          <ul className="divide-y divide-[#1A1A1A]" data-testid="codes-list">
            {codes.map((c) => (
              <li
                key={c.code}
                className="flex items-center justify-between gap-4 px-4 md:px-6 py-4"
                data-testid={`code-row-${c.code}`}
              >
                <div className="min-w-0">
                  <p className="font-heading text-lg text-[#FFD700] tracking-wider truncate">
                    {c.code}
                  </p>
                  <p className="text-gray-400 text-sm truncate">{c.name || "—"}</p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Ticket className="h-3.5 w-3.5 text-[#FFD700]" />
                    {c.uses} köp
                  </span>
                  <Button
                    onClick={() => handleDelete(c.code)}
                    disabled={deletingCode === c.code}
                    variant="outline"
                    size="sm"
                    className="border-[#333] text-gray-400 hover:text-red-400 hover:border-red-500 rounded-sm"
                    data-testid={`delete-code-${c.code}`}
                  >
                    {deletingCode === c.code ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
